import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import "./admin.css";

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://investor-backend-s4w5.onrender.com");

export default function AdminDashboard() {
  const [data, setData] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/investments`);
      setData(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const approveQuarterlyInterest = async (investmentId, interestId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/investments/approve-quarterly-interest/${investmentId}/${interestId}`,
      );
      alert("Quarterly interest approved ✅");
      loadData();
    } catch (err) {
      console.log(err);
      alert("Quarterly interest approval failed ❌");
    }
  };

  const formatAmount = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const approvedData = useMemo(() => {
    return data.filter((item) => item.accountVerified && item.adminApproved);
  }, [data]);

  const pendingInterestRows = useMemo(() => {
    return data.flatMap((inv) =>
      (inv.quarterlyInterests || [])
        .filter((q) => !q.adminApproved)
        .map((q) => ({
          investmentId: inv._id,
          interestId: q._id,
          investorId: inv.investorId,
          customerName: inv.customerName,
          depositAmount: inv.depositAmount,
          interestRate: inv.interestRate,
          quarterLabel: q.quarterLabel,
          amount: q.amount,
          paidDate: q.paidDate,
        })),
    );
  }, [data]);

  const approvedInterestRows = useMemo(() => {
    return approvedData.flatMap((inv) =>
      (inv.quarterlyInterests || [])
        .filter((q) => q.adminApproved)
        .map((q) => ({
          investmentId: inv._id,
          interestId: q._id,
          investorId: inv.investorId,
          customerName: inv.customerName,
          depositAmount: inv.depositAmount,
          interestRate: inv.interestRate,
          quarterLabel: q.quarterLabel,
          amount: q.amount,
          paidDate: q.paidDate,
        })),
    );
  }, [approvedData]);

  const totalInvestment = approvedData.reduce(
    (sum, i) => sum + Number(i.depositAmount || 0),
    0,
  );

  const totalInterest = approvedInterestRows.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0,
  );

  const totalInvestors = approvedData.length;

  const getFYandQuarter = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();

    let fyStartYear;
    let quarter;

    if (month >= 4 && month <= 6) {
      fyStartYear = year;
      quarter = "Q1";
    } else if (month >= 7 && month <= 9) {
      fyStartYear = year;
      quarter = "Q2";
    } else if (month >= 10 && month <= 12) {
      fyStartYear = year;
      quarter = "Q3";
    } else {
      fyStartYear = year - 1;
      quarter = "Q4";
    }

    const fy = `${fyStartYear}-${String(fyStartYear + 1).slice(2)}`;
    return { fy, quarter };
  };

  const fyData = useMemo(() => {
    const grouped = {};

    approvedData.forEach((item) => {
      const result = getFYandQuarter(item.depositDate);
      if (!result) return;

      const { fy, quarter } = result;

      if (!grouped[fy]) {
        grouped[fy] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
      }

      grouped[fy][quarter] += Number(item.depositAmount || 0);
    });

    return grouped;
  }, [approvedData]);

  useEffect(() => {
    if (!selectedFY && Object.keys(fyData).length > 0) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const currentFY =
        currentMonth >= 4
          ? `${currentYear}-${String(currentYear + 1).slice(2)}`
          : `${currentYear - 1}-${String(currentYear).slice(2)}`;

      if (fyData[currentFY]) {
        setSelectedFY(currentFY);
      } else {
        setSelectedFY(Object.keys(fyData)[0]);
      }
    }
  }, [fyData, selectedFY]);

  const chartData = [
    { name: "Q1", value: fyData[selectedFY]?.Q1 || 0 },
    { name: "Q2", value: fyData[selectedFY]?.Q2 || 0 },
    { name: "Q3", value: fyData[selectedFY]?.Q3 || 0 },
    { name: "Q4", value: fyData[selectedFY]?.Q4 || 0 },
  ];

  const barColors = ["#2563eb", "#0ea5e9", "#14b8a6", "#8b5cf6"];

  const upcoming = approvedData.filter((i) => {
    if (!i.maturityDate) return false;
    const diff = new Date(i.maturityDate) - new Date();
    return diff > 0 && diff < 1000 * 60 * 60 * 24 * 180;
  });

  return (
    <>
      <Navbar />

      <div className="dashboard">
        <div className="left">
          <div className="card stat-card investment-card">
            <div className="stat-title">💰 Total Investment</div>
            <div className="stat-value">₹ {formatAmount(totalInvestment)}</div>
          </div>

          <div className="card stat-card interest-card">
            <div className="stat-title">📈 Total Approved Interest</div>
            <div className="stat-value">₹ {formatAmount(totalInterest)}</div>
          </div>

          <div className="card stat-card investor-card">
            <div className="stat-title">👤 Approved Investors</div>
            <div className="stat-value">{totalInvestors}</div>
          </div>

          <div className="card stat-card pending-card">
            <div className="stat-title">🧾 Pending Interest Approvals</div>
            <div className="stat-value">{pendingInterestRows.length}</div>
          </div>
        </div>

        <div className="center">
          <div className="section-card chart-card">
            <div className="section-header">
              <h3>Quarterly Investment ({selectedFY || "N/A"})</h3>

              <select
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                className="fy-select"
              >
                {Object.keys(fyData).length > 0 ? (
                  Object.keys(fyData).map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))
                ) : (
                  <option value="">No FY Data</option>
                )}
              </select>
            </div>

            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="barGradient1"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#2563eb"
                        stopOpacity={0.95}
                      />
                      <stop
                        offset="95%"
                        stopColor="#93c5fd"
                        stopOpacity={0.55}
                      />
                    </linearGradient>
                    <linearGradient
                      id="barGradient2"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#0ea5e9"
                        stopOpacity={0.95}
                      />
                      <stop
                        offset="95%"
                        stopColor="#bae6fd"
                        stopOpacity={0.55}
                      />
                    </linearGradient>
                    <linearGradient
                      id="barGradient3"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#14b8a6"
                        stopOpacity={0.95}
                      />
                      <stop
                        offset="95%"
                        stopColor="#99f6e4"
                        stopOpacity={0.55}
                      />
                    </linearGradient>
                    <linearGradient
                      id="barGradient4"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#8b5cf6"
                        stopOpacity={0.95}
                      />
                      <stop
                        offset="95%"
                        stopColor="#ddd6fe"
                        stopOpacity={0.55}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e2e8f0"
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#64748b"
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#64748b"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(148, 163, 184, 0.12)" }}
                    contentStyle={{
                      background: "#ffffff",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                    }}
                    formatter={(value) => `₹ ${formatAmount(value)}`}
                  />
                  <Bar dataKey="value" radius={[12, 12, 0, 0]} maxBarSize={70}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`url(#barGradient${index + 1})`}
                        stroke={barColors[index]}
                        strokeWidth={1}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card big highlight-card">
            Total Paid Interest: ₹ {formatAmount(totalInterest)}
          </div>

          <div className="approval-section section-card">
            <h3>Pending Quarterly Interest Approvals</h3>

            <div className="table-wrapper">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Investor ID</th>
                    <th>Name</th>
                    <th>Quarter</th>
                    <th>Amount</th>
                    <th>Paid Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInterestRows.length > 0 ? (
                    pendingInterestRows.map((row) => (
                      <tr key={row.interestId}>
                        <td>{row.investorId || "-"}</td>
                        <td>{row.customerName || "-"}</td>
                        <td>{row.quarterLabel || "-"}</td>
                        <td>₹ {formatAmount(row.amount)}</td>
                        <td>{formatDate(row.paidDate)}</td>
                        <td>
                          <button
                            className="approve-btn"
                            onClick={() =>
                              approveQuarterlyInterest(
                                row.investmentId,
                                row.interestId,
                              )
                            }
                          >
                            Approve
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        No pending quarterly interest approvals
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="right">
          <div className="section-card">
            <h3>Upcoming Maturity (Next 6 Months)</h3>

            <div className="table-wrapper">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Investor ID</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {upcoming.length > 0 ? (
                    upcoming.map((item) => (
                      <tr key={item._id}>
                        <td>{item.investorId || "-"}</td>
                        <td>{item.customerName || "-"}</td>
                        <td>{formatDate(item.maturityDate)}</td>
                        <td>
                          ₹{" "}
                          {formatAmount(
                            item.maturityAmount || item.depositAmount || 0,
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">No upcoming maturity</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="approval-section section-card">
            <h3>Paid Quarterly Interest Entries</h3>

            <div className="table-wrapper">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Investor ID</th>
                    <th>Name</th>
                    <th>Quarter</th>
                    <th>Amount</th>
                    <th>Paid Date</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedInterestRows.length > 0 ? (
                    approvedInterestRows
                      .slice()
                      .sort(
                        (a, b) => new Date(b.paidDate) - new Date(a.paidDate),
                      )
                      .map((row) => (
                        <tr key={row.interestId}>
                          <td>{row.investorId || "-"}</td>
                          <td>{row.customerName || "-"}</td>
                          <td>{row.quarterLabel || "-"}</td>
                          <td>₹ {formatAmount(row.amount)}</td>
                          <td>{formatDate(row.paidDate)}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="5">No approved interest entries</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="note-box">
            <strong>Note:</strong>
            <p>
              Investment entries will reflect within <b>6 working days</b>.
            </p>
            <p>
              Premature withdrawal penalty is <b>15%</b>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
